import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";

import {
  GET,
  GET_HEADCOUNT_REPORT,
  GET_HEADCOUNT_REPORT_URL,
  GET_STATISTICS_REPORT_URL,
  GET_ATTRITION_RATE_REPORT_URL,
  REPORT_FETCH_CERTIFIED_PRE_FOUNDATIONAL,
  REPORT_FETCH_CERTIFIED_FOUNDATIONAL,
  REPORT_FETCH_CERTIFIED_ADVANCED,
  REPORT_FETCH_CERTIFIED_EXPERT,
  REPORT_FETCH_ATTRITION_RATE,
  FETCH_STATISTICS_REPORT,
  FETCH_ADDED_REPORT_DATA,
  SUPERSET_REPORT_LIST,
  SUPERSET_REPORT_LIST_URL,
  SUPERSET_COURSE_REPORT_LIST,
  SUPERSET_COURSE_REPORT_LIST_URL,
  TRAINING_IMPRESSION_REPORT,
  TRAINING_IMPRESSION_REPORT_URL,
} from "../../Api";
import apiRequest from "../../Api/connector";
import { STATUS_200 } from "../../Api/constants";
import { SomethingWentWrong } from "../../utils/Constant";
import { showToast } from "../../utils/helper/helper";

interface ReportState {
  reportData: ReportDataItem[];
  loading: boolean;
  error: string | null;
  addedReportData: ReportDataItem[];
  addedError: string | null;
  certifiedPreFoundationalData: ReportDataItem[];
  certifiedFoundationalData: ReportDataItem[];
  certifiedAdvancedData: ReportDataItem[];
  certifiedExpertData: ReportDataItem[];
  statisticsReportData?: any;
  attritionRateReportData?: any;
  supersetRepostList: any;
  trainingImpressionReportData: TrainingImpressionData;
}

const initialState: ReportState = {
  reportData: [],
  loading: false,
  error: null,
  addedReportData: [],
  addedError: null,
  certifiedPreFoundationalData: [],
  certifiedFoundationalData: [],
  certifiedAdvancedData: [],
  certifiedExpertData: [],
  statisticsReportData: null,
  attritionRateReportData: null,
  trainingImpressionReportData: { data: [] },
  supersetRepostList: {},
};

export const fetchReportData = createAsyncThunk(
  GET_HEADCOUNT_REPORT,
  async ({
    domain_id,
    financial_year,
    sbu,
    chart_type = "headcount",
    level_id,
  }: {
    domain_id: string | number;
    financial_year: string | number;
    sbu: string;
    chart_type?: string;
    level_id?: string | number;
  }) => {
    const url = GET_HEADCOUNT_REPORT_URL(
      domain_id,
      financial_year,
      sbu,
      chart_type,
      level_id
    );
    const response = await apiRequest(GET, url);
    return response;
  }
);

export const fetchAddedReportData = createAsyncThunk(
  FETCH_ADDED_REPORT_DATA,
  async ({
    domain_id,
    financial_year,
    sbu,
    chart_type = "addition",
    level_id,
  }: {
    domain_id: string | number;
    financial_year: string | number;
    sbu: string;
    chart_type?: string;
    level_id?: string | number;
  }) => {
    const url = GET_HEADCOUNT_REPORT_URL(
      domain_id,
      financial_year,
      sbu,
      chart_type,
      level_id
    );
    const response = await apiRequest(GET, url);
    return response;
  }
);

export const fetchCertifiedPreFoundational = createAsyncThunk(
  REPORT_FETCH_CERTIFIED_PRE_FOUNDATIONAL,
  async ({
    domain_id,
    financial_year,
    sbu,
  }: {
    domain_id: string | number;
    financial_year: string | number;
    sbu: string;
  }) => {
    const url = GET_HEADCOUNT_REPORT_URL(
      domain_id,
      financial_year,
      sbu,
      "certified",
      1
    );
    const response = await apiRequest(GET, url);
    return response;
  }
);

export const fetchCertifiedFoundational = createAsyncThunk(
  REPORT_FETCH_CERTIFIED_FOUNDATIONAL,
  async ({
    domain_id,
    financial_year,
    sbu,
  }: {
    domain_id: string | number;
    financial_year: string | number;
    sbu: string;
  }) => {
    const url = GET_HEADCOUNT_REPORT_URL(
      domain_id,
      financial_year,
      sbu,
      "certified",
      2
    );
    const response = await apiRequest(GET, url);
    return response;
  }
);

export const fetchCertifiedAdvanced = createAsyncThunk(
  REPORT_FETCH_CERTIFIED_ADVANCED,
  async ({
    domain_id,
    financial_year,
    sbu,
  }: {
    domain_id: string | number;
    financial_year: string | number;
    sbu: string;
  }) => {
    const url = GET_HEADCOUNT_REPORT_URL(
      domain_id,
      financial_year,
      sbu,
      "certified",
      3
    );
    const response = await apiRequest(GET, url);
    return response;
  }
);

export const fetchCertifiedExpert = createAsyncThunk(
  REPORT_FETCH_CERTIFIED_EXPERT,
  async ({
    domain_id,
    financial_year,
    sbu,
  }: {
    domain_id: string | number;
    financial_year: string | number;
    sbu: string;
  }) => {
    const url = GET_HEADCOUNT_REPORT_URL(
      domain_id,
      financial_year,
      sbu,
      "certified",
      4
    );
    const response = await apiRequest(GET, url);
    return response;
  }
);

export const fetchStatisticsReport = createAsyncThunk(
  FETCH_STATISTICS_REPORT,
  async ({
    domain_id,
    financial_year,
  }: {
    domain_id: string;
    financial_year: string;
  }) => {
    const url = GET_STATISTICS_REPORT_URL(domain_id, financial_year);
    const response = await apiRequest(GET, url);
    return response.data;
  }
);

export const fetchAttritionRateReport = createAsyncThunk(
  REPORT_FETCH_ATTRITION_RATE,
  async ({
    domain_id,
    financial_year,
  }: {
    domain_id: string;
    financial_year: string;
  }) => {
    const url = GET_ATTRITION_RATE_REPORT_URL(domain_id, financial_year);
    const response = await apiRequest(GET, url);
    return response.data;
  }
);

export const getSupersetReportsList = createAsyncThunk(
  SUPERSET_REPORT_LIST,
  async ({ type, dashboard_id }: { type?: string; dashboard_id?: string }) => {
    const response = await apiRequest(
      GET,
      SUPERSET_REPORT_LIST_URL(type, dashboard_id)
    );
    return response.data;
  }
);

export const getTrainingImpressionReport = createAsyncThunk(
  TRAINING_IMPRESSION_REPORT,
  async ({ from, to }: { from: string; to: string }) => {
    const response = await apiRequest(
      GET,
      TRAINING_IMPRESSION_REPORT_URL(from, to)
    );
    return response.data;
  }
);

export const getSupersetAssessmentReportsList = createAsyncThunk(
  SUPERSET_COURSE_REPORT_LIST,
  async ({
    type,
    dashboard_id,
    course_id,
    assessment_id,
  }: {
    type?: string;
    dashboard_id?: string;
    course_id?: string;
    assessment_id?: string;
  }) => {
    const response = await apiRequest(
      GET,
      SUPERSET_COURSE_REPORT_LIST_URL(
        type,
        dashboard_id,
        course_id,
        assessment_id
      )
    );
    return response.data;
  }
);

const ReportSlice = createSlice({
  name: "report",
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchReportData.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchReportData.fulfilled, (state, action) => {
        state.loading = false;
        state.reportData = action.payload;
      })
      .addCase(fetchReportData.rejected, (state, action) => {
        state.loading = false;
        try {
          const error = JSON.parse(action.error.message);
          state.error = error.message;
        } catch (e) {
          state.error = SomethingWentWrong;
        }
      })
      .addCase(fetchAddedReportData.pending, state => {
        state.loading = true;
        state.addedError = null;
      })
      .addCase(fetchAddedReportData.fulfilled, (state, action) => {
        state.loading = false;
        state.addedReportData = action.payload;
      })
      .addCase(fetchAddedReportData.rejected, (state, action) => {
        state.loading = false;
        try {
          const error = JSON.parse(action.error.message);
          state.addedError = error.message;
        } catch (e) {
          state.addedError = SomethingWentWrong;
        }
      })
      .addCase(fetchCertifiedPreFoundational.pending, state => {
        state.loading = true;
      })
      .addCase(fetchCertifiedPreFoundational.fulfilled, (state, action) => {
        state.loading = false;
        state.certifiedPreFoundationalData = action.payload;
      })
      .addCase(fetchCertifiedPreFoundational.rejected, state => {
        state.loading = false;
      })
      .addCase(fetchCertifiedFoundational.pending, state => {
        state.loading = true;
      })
      .addCase(fetchCertifiedFoundational.fulfilled, (state, action) => {
        state.loading = false;
        state.certifiedFoundationalData = action.payload;
      })
      .addCase(fetchCertifiedFoundational.rejected, state => {
        state.loading = false;
      })
      .addCase(fetchCertifiedAdvanced.pending, state => {
        state.loading = true;
      })
      .addCase(fetchCertifiedAdvanced.fulfilled, (state, action) => {
        state.loading = false;
        state.certifiedAdvancedData = action.payload;
      })
      .addCase(fetchCertifiedAdvanced.rejected, state => {
        state.loading = false;
      })
      .addCase(fetchCertifiedExpert.pending, state => {
        state.loading = true;
      })
      .addCase(fetchCertifiedExpert.fulfilled, (state, action) => {
        state.loading = false;
        state.certifiedExpertData = action.payload;
      })
      .addCase(fetchCertifiedExpert.rejected, state => {
        state.loading = false;
      })
      .addCase(fetchStatisticsReport.pending, state => {
        state.loading = true;
      })
      .addCase(fetchStatisticsReport.fulfilled, (state, action) => {
        state.loading = false;
        state.statisticsReportData = action.payload;
      })
      .addCase(fetchStatisticsReport.rejected, state => {
        state.loading = false;
      })
      .addCase(fetchAttritionRateReport.pending, state => {
        state.loading = true;
      })
      .addCase(fetchAttritionRateReport.fulfilled, (state, action) => {
        state.loading = false;
        state.attritionRateReportData = action.payload;
      })
      .addCase(fetchAttritionRateReport.rejected, state => {
        state.loading = false;
      })
      .addCase(getSupersetReportsList.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getSupersetReportsList.fulfilled, (state, action) => {
        state.loading = false;
        const { status_code, message } = action.payload;
        if (status_code === STATUS_200) showToast(message, "success");
      })

      .addCase(getSupersetReportsList.rejected, (state, action) => {
        try {
          const error = JSON.parse(action.error.message);
          state.error = error.message;
          showToast(state?.error, "error");
        } catch (e) {
          state.error = SomethingWentWrong;
        }
      })
      .addCase(getTrainingImpressionReport.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        getTrainingImpressionReport.fulfilled,
        (state, action: PayloadAction<TrainingImpressionData>) => {
          state.loading = false;
          state.trainingImpressionReportData = action.payload;
        }
      )
      .addCase(getTrainingImpressionReport.rejected, (state, action) => {
        try {
          const error = JSON.parse(action.error.message);
          state.error = error.message;
          showToast(state?.error, "error");
        } catch (e) {
          state.error = SomethingWentWrong;
        }
      });
  },
});

export default ReportSlice.reducer;
